import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import clsx from "clsx";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import { IDropResult } from "../../types/plannerTypes";
import PlannerTimelineLoading from "../../planner/PlannerTimelineLoading";
import { Targets } from "../../../../common/dndHelper";
import ErrorMessage from "../../../shared/messages/ErrorMessage";
import AdHocRequestsTable from "./AdHocRequestsTable";
import { useDrop } from "react-dnd";
import { ConflictDialogData } from "../../types/conflictNoticeTypes";
import ConflictNotice from "../../conflictNotices/ConflictNotice";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../store/rootReducer";
import { getData, getRoles, persistName, setData } from "../../helpers/transferScheduleHelpers";
import { consts } from "../../../../common/consts";
import { checkAcuity, checkuserpermission, checkuserpermissionforAdhoc } from "../../conflictNotices/conflictNoticeHelper";
import { getPlannerLoading } from "../plannerHelpers";
import { applyLocalChangesForTimelineMoveToRequested } from "../../../../store/modules/plannerSlice";
import { setLoading } from "../../../../store/modules/plannerLoadingSlice";

const AdHocRequests = (props: any) => {
  const {
    requestList,
    roles,
    transferMode,
    updatedTransfers,
    setCurrentTransferId,
    setCurrentBookingId,
    setShowVehicleModal,
    setCurrentTransferData,
    setShowScheduleModal,
    setShowScheduleModalAssignVehicleOption,
  } = props;
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [conflictDialogData, setConflictDialogData] = React.useState<ConflictDialogData | null>(null);
  const isConflictDialogOpen = conflictDialogData !== null;
  const { authStore } = useSelector((state: RootState) => state);
  const [role, setRoles] = React.useState<any>(getRoles(authStore));
  const dispatch = useDispatch();
  const classes = useStyles();
  const plannerStore = useSelector((state: RootState) => state.plannerStore);
  const [{ canDrop, isOver }, drop] = useDrop(() => ({
    accept: "TransferRequest",
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
      drop: (item, monitor) => {
          var superuser = (authStore.data?.role.split(",")?.some((p) => p == consts.Permissions.SMASuperUser));
          if (superuser !== true) {
              const userper = checkuserpermissionforAdhoc(roles.canRequestAdHocTransfer);
              if (userper !== null) {
                  setConflictDialogData(userper);
                  return null;
              }
          }

      const dropResult: IDropResult = {
        target: Targets.AD_HOC_REQUESTS,
        removeTripFromPlanner,
      };
      return dropResult;
    },
  }));


  const removeTripFromPlanner = React.useCallback(
    async ({ virtualTripId, tripPlannerId }: { virtualTripId: string; tripPlannerId: string }) => {
      try {
        if (getPlannerLoading()) {
          throw "Previous timeline update not finished";
        }
        setErrorMessage("");
        // apply local state changes
        dispatch(
          applyLocalChangesForTimelineMoveToRequested({
            tripPlannerId: tripPlannerId,
            virtualTripId: virtualTripId,
            type: Targets.AD_HOC_REQUESTS,
          })
        );
      } catch (error) {
        setErrorMessage(error as string); // "Error while applying timeline changes"
        dispatch(setLoading(false));
      }
    },
    [plannerStore]
  );
  
  return (
    <>
      {errorMessage && <ErrorMessage errorMessage={errorMessage} />}
      <PlannerTimelineLoading />
      <div className={classes.root}>
        <div className={classes.heading}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setSectionCollapsed(!sectionCollapsed)}
            className={classes.headerButton}
            disableRipple
          >
            {!sectionCollapsed ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
            <Typography variant="body1" component="div" className={classes.headerTitle}>
              Ad-hoc Requests
            </Typography>
            {sectionCollapsed && (
              <Typography variant="body1" component="div" className={classes.transfersCount}>
                {requestList.length} Transfers
              </Typography>
            )}
          </IconButton>
        </div>

        <Collapse in={!sectionCollapsed} timeout="auto" unmountOnExit>
          <div
            ref={drop}
            className={clsx(classes.dropzoneContainer, {
              [classes.dropzoneContainerBorder]: canDrop,
            })}
          >
            <div
              className={clsx(classes.dropzone, {
                [classes.dropzoneActive]: canDrop,
                [classes.dropzoneOver]: isOver,
              })}
            >
              Drag a transfer here to have SMA schedule it as an <strong>ad-hoc</strong> request (surcharges may apply)
            </div>
          </div>

          <AdHocRequestsTable
            transfers={requestList || []}
            updatedTransfers={updatedTransfers}
            roles={roles}
            transferMode={transferMode}
            setCurrentTransferId={setCurrentTransferId}
            setCurrentBookingId={setCurrentBookingId}
            setShowVehicleModal={setShowVehicleModal}
            setErrorMessage={setErrorMessage}
            setCurrentTransferData={setCurrentTransferData}
            setShowScheduleModal={setShowScheduleModal}
            setShowScheduleModalAssignVehicleOption={setShowScheduleModalAssignVehicleOption}
            setConflictDialogData={setConflictDialogData}
               />
        </Collapse>
          </div>
          {isConflictDialogOpen ? (
              <ConflictNotice
                  isDialogOpen={isConflictDialogOpen}
                  onCancel={() => setConflictDialogData(null)}
                  conflictType={conflictDialogData!!.conflictType}
                  plannerBreakDuration={conflictDialogData?.tripPlanner?.plannerBreakDuration}
                  plannerBreakStartTime={conflictDialogData?.tripPlanner?.plannerBreakStartTime}
                  plannerMaxBreakDelay={conflictDialogData?.tripPlanner?.plannerMaxBreakDelay}
              />
          ) : null}
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: 15,
    paddingBottom: 17,
    backgroundColor: "#00152E",
  },
  heading: {},
  headerTitle: {
    fontSize: 21,
    fontWeight: 400,
    marginLeft: 15,
  },
  headerButton: {
    paddingLeft: 13,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  transfersCount: {
    fontSize: 21,
    fontWeight: 500,
    opacity: 0.36,
    marginLeft: 15,
  },
  table: {
    width: "100%",
    tableLayout: "fixed",
  },
  dropzoneContainer: {
    backgroundColor: "#02489D",
    padding: "12px 18px",
    marginTop: 25,
    border: "3px solid transparent",
  },
  dropzoneContainerBorder: {
    border: "3px solid #F4B827",
  },
  dropzone: {
    backgroundColor: "#02489D",
    border: "2px dashed rgba(255, 255, 255, 0.330163)",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "19px",
    paddingTop: 15,
    paddingBottom: 19,
    textAlign: "center",
    opacity: 0.62,
  },
  dropzoneActive: {
    opacity: 0.9,
  },
  dropzoneOver: {
    opacity: 1,
  },
  tableBody: {
    backgroundColor: "#002047",
    cursor: "pointer",
    "& > *": {
      fontSize: 18,
      fontWeight: 500,
    },
    height: 33,
  },
  animatedUpdatedItem: {
    animation: `$updatedTransferEffect 60000ms ${theme.transitions.easing.easeInOut}`,
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.08) !important",
    },
  },
  "@keyframes updatedTransferEffect": {
    "0%": { backgroundColor: "#F4B8273A" },
    "100%": { backgroundColor: "#002047" },
  },
  tableCell: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    height: 33,
  },
  needIconCenter: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    minHeight: 33,
    border: 0,
  },
  tableHead: {
    backgroundColor: "#02489D",
    "& > *": {
      fontSize: 18,
      fontWeight: 400,
      opacity: 0.3,
    },

    "& > th": {
      padding: "6px 24px 16px 16px",
    },
  },
}));

export default AdHocRequests;
